import { itx } from "../../../../src/translationItx/itx";
import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg } from "../C1_FurnitureSvg";
import { C1_FurnitureIcon } from "../C1_FurnitureIcon";

const DEFAULT_WIDTH = 1.8;
const DEFAULT_HEIGHT = 0.9;
const ARMREST_THICKNESS_M = 0.3;
const WALL_GAP_M = 0.1;

const Sofa: C1_FurnitureDefinition = {
  id: "sofa",
  icon: <C1_FurnitureIcon kind="sofa-2-seat" />,
  description: { en: "Sofa", de: "Sofa" },
  category: RoomType.Living,
  defaultWidth: DEFAULT_WIDTH,
  defaultHeight: DEFAULT_HEIGHT,
  grabsW: true,
  grabsH: true,
  originXPercentage: 50,
  originYPercentage: 0,
  autoRotate: true,
  properties: [
    {
      key: "numberOfSeats",
      get label() { return itx("c1.ui.Sitzplatze", "Sitzplätze"); },
      type: "number",
      enum: {
        0: "automatisch",
        1: "1",
        2: "2",
        3: "3",
        4: "4",
      },
    },
  ],

  Renderer: (props: C1_FurnitureRendererProps) => {
    const { width, height, rotation, PX, strokeWidth, strokeColor, fillColor, showDetails } = props;

    const finalRotation = rotation ?? 0;
    const finalStrokeWidth = strokeWidth ?? 2;
    const finalStrokeColor = strokeColor ?? "#000000";
    const finalFillColor = fillColor ?? "#ffffff";

    const armrestThickness = ARMREST_THICKNESS_M * PX;
    const backrestThickness = 14;
    const geometryOffsetY = WALL_GAP_M * PX;

    // Die SVG-Geometrie entspricht direkt der Möbelgröße.
    // Dadurch wird nicht nachträglich gestreckt.
    const svgWidth = width;
    const svgHeight = height;

    const seatX = armrestThickness;
    const seatY = backrestThickness;
    const seatWidth = Math.max(0, svgWidth - armrestThickness * 2);
    const seatHeight = Math.max(0, svgHeight - backrestThickness);

    const numberOfSeats = props["numberOfSeats"] as number | undefined;
    const finalNumberOfSeats = numberOfSeats || Math.round((width / (PX ?? 1) - 0.6) / 0.7);

    const cornerRadius = Math.min(12, armrestThickness / 2, backrestThickness / 2);

    return (
      <C1_FurnitureSvg
        PX={PX}
        width={width}
        height={height}
        rotation={finalRotation}
        viewBoxWidth={svgWidth}
        viewBoxHeight={svgHeight}
        preserveAspectRatio="none"
        originXPercentage={50}
        originYPercentage={0}
      >
        <g transform={`translate(0 ${geometryOffsetY})`}>
          {/* Sitzfläche */}
          <rect
            x={seatX}
            y={seatY}
            width={seatWidth}
            height={seatHeight}
            rx={Math.min(8, seatWidth / 10, seatHeight / 10)}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth}
          />

          {/* Rückenlehne */}
          <rect
            x={0}
            y={0}
            width={svgWidth}
            height={backrestThickness}
            rx={cornerRadius}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth * 2}
          />

          {/* Linke Armlehne */}
          <rect
            x={0}
            y={0}
            width={armrestThickness}
            height={svgHeight}
            rx={cornerRadius}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth * 2}
          />

          {/* Rechte Armlehne */}
          <rect
            x={svgWidth - armrestThickness}
            y={0}
            width={armrestThickness}
            height={svgHeight}
            rx={cornerRadius}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth * 2}
          />

          {showDetails &&
            finalNumberOfSeats > 1 &&
            Array.from({ length: finalNumberOfSeats - 1 }).map((_, index) => {
              const x = seatX + (seatWidth * (index + 1)) / finalNumberOfSeats;

              return (
                <line
                  key={index}
                  x1={x}
                  y1={seatY}
                  x2={x}
                  y2={svgHeight}
                  stroke={finalStrokeColor}
                  strokeWidth={finalStrokeWidth}
                />
              );
            })}
        </g>
      </C1_FurnitureSvg>
    );
  },
};

export default Sofa;
